import { useEffect, useRef, useState, useCallback } from 'react';
import {
  doc, onSnapshot, runTransaction, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { getClientId } from '../lib/clientId';

/**
 * useEditLock — soft-lock for concurrent editing on the same list.
 *
 * Originally built for shared-list collaboration ("Anna is editing,
 * Tom is read-only"). Walter 2026-05-16: extended to also cover
 * SOLO multi-device editing — your desktop and your phone shouldn't
 * both write to the same list's content at the same time either, or
 * the stale-base last-write-wins problem clobbers your edits.
 *
 * The lock holder is identified by `(uid, clientId)`:
 *   - Same uid + same clientId  → fully you, allow editing
 *   - Same uid + different clientId → "your other device is editing"
 *     (the new per-device case)
 *   - Different uid → another user (sharing case)
 *
 * clientId is per browser install (localStorage-backed, see
 * lib/clientId.js). Survives reloads + PWA restarts; lost on
 * cache-clears / reinstalls (which is fine — old lock auto-stales
 * within 30s).
 *
 * Mechanics:
 *   - List doc has an `editLock` field: { uid, clientId, name,
 *     startedAt, lastActivityAt }
 *   - Acquire is a Firestore transaction (atomic check + write) so
 *     simultaneous take-over presses can't both succeed
 *   - Heartbeat every HEARTBEAT_MS while the local user is the holder,
 *     refreshing lastActivityAt
 *   - Release on blur, idle, unmount, or page hide
 *   - Stale lock = lastActivityAt older than STALE_AFTER_MS
 *
 * Cost: a heartbeat write every 4s while actively typing. For solo
 * single-device users this is wasted but cheap (~75 writes/day for an
 * average user → fractions of a cent/year at scale).
 *
 * Caller usage:
 *   const lock = useEditLock({
 *     ownerUid, boardId, list, currentUid, currentUserName, enabled
 *   });
 *   // lock.heldBy           — { uid, name, isOtherDevice } | null
 *   // lock.heldByMe         — true ONLY when (uid, clientId) both match
 *   // lock.canEdit          — true if I hold the lock OR no one does
 *   // lock.acquire()        — try to acquire / take over
 *   //                          → { acquired, contended } (see docstring)
 *   // lock.release()        — release if I hold
 *   // lock.touch()          — refresh lastActivityAt (call on each keystroke)
 *
 * `enabled` lets callers disable the hook when not needed (e.g. on
 * Plus tier when Yjs handles concurrency).
 */

// Heartbeat cadence is the dominant cost driver here — every tick while
// a user is typing in a shared list is one Firestore write. 5s strikes
// a good balance: on a heavy-typer profile (~300 edit-seconds/day),
// that's ~22k writes/year ≈ $0.04/user/year COGS. Going lower (1s)
// costs ~5x more for marginal UX gain. Going higher (10s) saves a bit
// more but the take-over button takes too long to re-enable after the
// holder stops typing.
//
// ACTIVE_TYPING_MS must be >= HEARTBEAT_MS or the activelyTyping flag
// will flicker false between heartbeats. We keep ~1s of slack.
const HEARTBEAT_MS = 4 * 1000;           // refresh lock at most once every 4s while typing
const ACTIVE_TYPING_MS = 5 * 1000;       // "actively typing" if lastActivityAt within 5s
const STALE_AFTER_MS = 30 * 1000;        // lock considered abandoned after 30s no activity
const IDLE_RELEASE_MS = 30 * 1000;       // auto-release if user goes 30s without typing

// Module-level: tracks the lock currently held by this tab's user.
// When a different useEditLock instance acquires (for a different list),
// the old lock is released first — a user holds at most one list's lock
// per tab. Without this, switching from list A to list B leaves A
// locked for the full IDLE_RELEASE_MS window, blocking other editors
// on lists that the user has clearly stopped editing.
//
// `release` is the release function from the hook that's currently
// holding the lock. It's safe to call even after that hook re-renders
// because release is wrapped in useCallback with stable deps.
const activeLock = { listId: null, release: null };

export function useEditLock({
  ownerUid,
  boardId,
  list,
  currentUid,
  currentUserName,
  enabled = true,
  // Optional: called when this user's lock gets force-acquired by
  // someone else (different uid OR same uid different clientId).
  // Receives the new holder's name (or "Your other device" for the
  // same-uid case). Parent uses this to surface a toast/banner so
  // the displaced user knows what just happened.
  onKicked,
}) {
  const listId = list?.id || null;
  const editLockRaw = list?.editLock || null;
  // Per-device identifier — distinguishes desktop from phone for the
  // same Firebase Auth user. Stable across reloads, lost on
  // cache-clear / PWA reinstall.
  const currentClientId = getClientId();

  // Track the last time WE touched the keyboard, so we know when to
  // release (user walked away) vs heartbeat (user is still typing).
  const lastTouchRef = useRef(0);
  const heartbeatTimerRef = useRef(null);
  const idleTimerRef = useRef(null);

  // Force a re-render every second while a lock is held. The
  // activelyTyping flag below is computed from `Date.now() -
  // lastActivityAt`, which goes stale without a fresh render. The
  // tick keeps the UI's view of "they stopped typing 4 seconds ago"
  // accurate without requiring a Firestore doc update.
  const [, forceTick] = useState(0);
  // Re-keyed on the holder uid only — when that flips, restart the
  // tick. Everything else is intentionally excluded from deps;
  // forceTick is a stable setState identity per React's contract.
  const editLockHolderUid = editLockRaw?.uid || null;
  useEffect(() => {
    if (!editLockHolderUid) return undefined;
    const id = setInterval(() => forceTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [editLockHolderUid]);

  // State derived from the list doc snapshot — what the OTHER user
  // sees about the lock. We don't need an extra subscription because
  // `list` already comes from a Firestore snapshot upstream.
  // Walter 2026-05-16: heldByMe now requires BOTH uid + clientId to
  // match, so "Walter on desktop" and "Walter on iPhone" are treated
  // as different lock holders. `isOtherDevice` flag on heldBy lets the
  // UI distinguish "your phone is editing" from "another user is
  // editing" — see ListPanel / FocusedPanel for the read.
  const heldBy = computeHeldBy(editLockRaw, currentUid, currentClientId);
  const heldByMe = !!(
    heldBy
    && heldBy.uid === currentUid
    && heldBy.clientId === currentClientId
  );
  const canEdit = !heldBy || heldByMe;

  // Detect "I just got kicked" — I was the holder on the previous
  // render, now someone else (different uid OR different clientId on
  // the same uid) is. Fire onKicked once per kick.
  // Walter 2026-05-16: track the previous (uid, clientId) tuple as a
  // string so we can detect same-uid-different-clientId takeovers too
  // (your other device just grabbed the lock).
  const prevHolderKeyRef = useRef(null);
  const myKey = `${currentUid}::${currentClientId}`;
  const heldByKey = heldBy ? `${heldBy.uid}::${heldBy.clientId}` : null;
  useEffect(() => {
    const prev = prevHolderKeyRef.current;
    if (prev === myKey && heldByKey && heldByKey !== myKey) {
      // Was me, now someone else (could be another user, or my own
      // other device). Use a friendly label for same-uid case.
      const label = heldBy?.isOtherDevice
        ? 'Your other device'
        : (heldBy?.name || 'someone');
      onKicked?.(label);
    }
    prevHolderKeyRef.current = heldByKey;
  }, [heldByKey, myKey, onKicked, heldBy?.isOtherDevice, heldBy?.name]);

  // Lock-doc reference helper. We wrap so the hook signature stays
  // stable even if the path layout changes later (e.g. moving lists
  // out of the user subtree).
  const lockRef = useCallback(() => {
    if (!ownerUid || !boardId || !listId) return null;
    return doc(db, 'users', ownerUid, 'boards', boardId, 'lists', listId);
  }, [ownerUid, boardId, listId]);

  /**
   * acquire — try to take ownership of the lock.
   *
   * Returns a DISCRIMINATED result, not a bare boolean, so callers
   * can tell a genuine takeover from a transient failure:
   *
   *   { acquired: true,  contended: false } — the lock is now ours
   *       (we already held it, it was stale/free, or `force` won it).
   *   { acquired: false, contended: true  } — real contention: the
   *       acquire transaction reached the server and found a FRESH
   *       lock held by another device/user, and `force` was not set.
   *   { acquired: false, contended: false } — the transaction never
   *       completed (offline, a network blip, the list doc missing
   *       from the snapshot). This is NOT a takeover — callers must
   *       not show "another device took the lock" or roll back the
   *       user's typing on this result.
   *
   * Walter 2026-05-21: the bare boolean conflated contention with
   * transient failure, so a network blip right after page load
   * surfaced a phantom "another device took the lock" toast with no
   * second device involved. `contended` is the precise signal.
   *
   * Idempotent: if I already hold it, returns acquired:true without a
   * meaningful write. `force:true` (the "take over editing" button)
   * acquires even over a fresh foreign lock.
   */
  const acquire = useCallback(async ({ force = false } = {}) => {
    if (!enabled || !currentUid) return { acquired: false, contended: false };
    const ref = lockRef();
    if (!ref) return { acquired: false, contended: false };

    try {
      const outcome = await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists()) return 'unavailable';
        const cur = snap.data().editLock || null;
        // "Mine" now requires BOTH uid and clientId to match. Same
        // user on a different device counts as someone else holding
        // the lock — they have to acquire it (or force takeover).
        const isMine = cur
          && cur.uid === currentUid
          && cur.clientId === currentClientId;
        const stale = cur && isStale(cur);

        if (isMine) {
          tx.update(ref, {
            'editLock.lastActivityAt': serverTimestamp(),
          });
          return 'acquired';
        }

        if (cur && !stale && !force) {
          // Genuine contention — a non-stale lock held by another
          // device/user. This is the ONLY outcome that means
          // "someone else is editing." Reaching here proves the
          // transaction got a fresh server read.
          return 'contended';
        }

        tx.update(ref, {
          editLock: {
            uid: currentUid,
            clientId: currentClientId,
            name: currentUserName || '',
            startedAt: serverTimestamp(),
            lastActivityAt: serverTimestamp(),
          },
        });
        return 'acquired';
      });
      // Refresh activity timestamp on successful acquire so the
      // idle-release timer doesn't fire immediately. Without this,
      // a freshly-acquired lock (especially via "take over") would
      // get auto-released on the first idle tick because lastTouchRef
      // was still 0 from initialization → looked like 30s had elapsed.
      if (outcome === 'acquired') {
        lastTouchRef.current = Date.now();
        // One-lock-at-a-time: if we previously held a lock on a
        // different list, release it now. Avoids the "two boxes
        // locked by me" stale-hold confusion when the user moves
        // between lists in the same session.
        if (activeLock.listId && activeLock.listId !== listId
            && typeof activeLock.release === 'function') {
          // Fire-and-forget; the previous release runs its own
          // transaction and we don't want to block this acquire on it.
          Promise.resolve(activeLock.release()).catch(() => {});
        }
        activeLock.listId = listId;
        activeLock.release = release;
      }
      return {
        acquired: outcome === 'acquired',
        contended: outcome === 'contended',
      };
    } catch (err) {
      // Filter the noisy failed-precondition errors that come from
      // Firestore's automatic transaction retry — they aren't real
      // failures (the retry succeeded), they're just the SDK logging
      // the conflict that triggered the retry. Real errors (rules
      // denials, network issues) still surface.
      if (err && err.code !== 'failed-precondition') {
        console.warn('[useEditLock] acquire failed:', err);
      }
      // Transaction never completed — couldn't reach the server.
      // NOT contention: do not let callers treat this as a takeover.
      return { acquired: false, contended: false };
    }
  }, [enabled, currentUid, currentUserName, currentClientId, lockRef, listId]);

  /**
   * release — drop the lock if I hold it. Safe to call always; if I
   * don't hold the lock the transaction is a no-op.
   */
  const release = useCallback(async () => {
    if (!enabled || !currentUid) return;
    const ref = lockRef();
    if (!ref) return;
    try {
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists()) return;
        const cur = snap.data().editLock || null;
        // Only release if BOTH uid and clientId match — don't drop
        // your other device's lock by accident.
        if (cur && cur.uid === currentUid && cur.clientId === currentClientId) {
          tx.update(ref, { editLock: null });
        }
      });
      // Clear the module-level pointer if we just released the
      // lock that was being tracked there.
      if (activeLock.listId === listId) {
        activeLock.listId = null;
        activeLock.release = null;
      }
    } catch (err) {
      if (err && err.code !== 'failed-precondition') {
        console.warn('[useEditLock] release failed:', err);
      }
    }
  }, [enabled, currentUid, currentClientId, lockRef, listId]);

  /**
   * touch — call on every keystroke. Records that the user is active.
   * The heartbeat tick reads this to decide whether to refresh the
   * server-side lastActivityAt.
   */
  const touch = useCallback(() => {
    lastTouchRef.current = Date.now();
  }, []);

  // Heartbeat: while we hold the lock AND the user is mid-keystroke,
  // refresh lastActivityAt on the server. The 1s cadence + tight
  // ACTIVE_TYPING_MS gate means lastActivityAt accurately reflects
  // "this user is typing right now" — which the take-over button on
  // other users' tabs reads to disable itself during active editing.
  useEffect(() => {
    if (!enabled || !heldByMe) {
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
      return undefined;
    }
    const tick = async () => {
      const ref = lockRef();
      if (!ref) return;
      // Only refresh while the user is actively typing. Once they
      // stop, lastActivityAt freezes — other tabs see it age out and
      // re-enable the take-over button after ACTIVE_TYPING_MS.
      const since = Date.now() - lastTouchRef.current;
      if (since > ACTIVE_TYPING_MS) return;
      try {
        await runTransaction(db, async (tx) => {
          const snap = await tx.get(ref);
          if (!snap.exists()) return;
          const cur = snap.data().editLock || null;
          // Heartbeat ONLY if both uid + clientId match — paranoid
          // guard against refreshing a lock that's been stolen.
          if (cur
              && cur.uid === currentUid
              && cur.clientId === currentClientId) {
            tx.update(ref, {
              'editLock.lastActivityAt': serverTimestamp(),
            });
          }
        });
      } catch (err) {
        // Swallow — heartbeat failures aren't user-visible
      }
    };
    const interval = setInterval(tick, HEARTBEAT_MS);
    heartbeatTimerRef.current = interval;
    return () => {
      clearInterval(interval);
      heartbeatTimerRef.current = null;
    };
  }, [enabled, heldByMe, currentUid, currentClientId, lockRef]);

  // Idle auto-release: if the user holds the lock but hasn't typed in
  // IDLE_RELEASE_MS, drop it so others aren't blocked.
  useEffect(() => {
    if (!enabled || !heldByMe) {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      return undefined;
    }
    const checkIdle = () => {
      const since = Date.now() - lastTouchRef.current;
      if (since > IDLE_RELEASE_MS) {
        release();
      } else {
        idleTimerRef.current = setTimeout(checkIdle, IDLE_RELEASE_MS - since + 1000);
      }
    };
    idleTimerRef.current = setTimeout(checkIdle, IDLE_RELEASE_MS);
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    };
  }, [enabled, heldByMe, release]);

  // Release on unmount only. The 30-second idle-release effect above
  // already handles abandoned tabs (user walks away, tab is still
  // open but they're not typing). visibilitychange-on-hidden was too
  // aggressive — switching to another window for a few seconds
  // dropped the lock instantly, so a co-editor switching tabs to
  // peek at someone else's edit never saw the lock badge.
  useEffect(() => {
    if (!enabled) return undefined;
    return () => {
      if (heldByMe) release();
    };
  }, [enabled, heldByMe, release]);

  return {
    heldBy,
    heldByMe,
    canEdit,
    acquire,
    release,
    touch,
  };
}

// Helper: shape the editLock field into a stable { uid, name,
// activelyTyping } | null. Returns null if the field is missing OR
// if the lock is stale (the holder hasn't been heard from in
// STALE_AFTER_MS — they're effectively gone, and we treat the list
// as unlocked).
//
// activelyTyping = the holder's lastActivityAt was within the last
// ACTIVE_TYPING_MS window. UI uses this to disable the "take over"
// button while the holder is in mid-keystroke.
// Walter 2026-05-16: now also returns clientId + isOtherDevice so
// callers can distinguish "your phone is editing" from "another user
// is editing." isOtherDevice = same uid as me but different clientId.
function computeHeldBy(editLock, currentUid, currentClientId) {
  if (!editLock || !editLock.uid) return null;
  if (isStale(editLock)) return null;
  const last = toMillis(editLock.lastActivityAt);
  const activelyTyping = !!(last && (Date.now() - last < ACTIVE_TYPING_MS));
  const isOtherDevice = !!(
    currentUid
    && editLock.uid === currentUid
    && editLock.clientId
    && editLock.clientId !== currentClientId
  );
  return {
    uid: editLock.uid,
    clientId: editLock.clientId || null,
    name: editLock.name || 'someone',
    activelyTyping,
    isOtherDevice,
  };
}

function isStale(editLock) {
  if (!editLock || !editLock.lastActivityAt) return true;
  const last = toMillis(editLock.lastActivityAt);
  if (!last) return false;
  return Date.now() - last > STALE_AFTER_MS;
}

function toMillis(t) {
  if (!t) return null;
  if (typeof t === 'number') return t;
  if (typeof t.toMillis === 'function') return t.toMillis();
  if (typeof t.seconds === 'number') return t.seconds * 1000;
  if (t instanceof Date) return t.getTime();
  return null;
}
